import React from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Card, Badge } from 'flowbite-react';
import { useDatabase } from '../../hooks/useDatabase';
import { useProblem } from '../../hooks/useProblem';
import EditableTitle from '../../components/EditableTitle';

const ProblemDetailPage: React.FC<{ overrideProblemId?: string }> = ({ overrideProblemId }) => {
  const { problemId: paramId } = useParams<{ problemId: string }>();
  const problemId = overrideProblemId || paramId;
  const { problem, loading, error } = useProblem(problemId);
  const { applyCommand, database } = useDatabase();

  const updateTitle = async (newTitle: string) => {
    if (!problemId) return;
    await applyCommand({ type: 'UpdateProblem', id: problemId, actions: [{ type: 'SetTitle', title: newTitle }] });
  };

  const updateDescription = async (newDescription: string) => {
    if (!problemId) return;
    await applyCommand({ type: 'UpdateProblem', id: problemId, actions: [{ type: 'SetDescription', description: newDescription || null }] });
  };

  const toggleResolved = async () => {
    if (!problemId || !problem) return;
    await applyCommand({ type: 'UpdateProblem', id: problemId, actions: [{ type: 'SetResolved', resolved: !problem.resolved }] });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error || !problem) {
    return (
      <div className="text-center py-12 text-cm-text-muted">
        {error || 'Problem not found'}
      </div>
    );
  }

  const reportedByName = problem.reportedBy && database
    ? (() => {
        const people = database.people();
        const person = people[problem.reportedBy];
        return person ? person.name() : problem.reportedBy;
      })()
    : null;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <EditableTitle value={problem.title} onSave={updateTitle} />
        <Badge color={problem.resolved ? 'success' : 'warning'}>
          {problem.resolved ? 'Resolved' : 'Open'}
        </Badge>
      </div>

      <Card>
        <h3 className="font-mono font-medium text-cm-text-primary mb-3">Details</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-cm-text-muted block mb-1">Description</label>
            <textarea
              className="w-full bg-cm-bg-elevated border border-cm-border-subtle rounded-lg p-3 text-cm-text-primary text-sm resize-y min-h-[80px]"
              value={problem.description || ''}
              onChange={(e) => updateDescription(e.target.value)}
              placeholder="What is the problem and why does it matter?"
            />
          </div>

          {reportedByName && (
            <div>
              <label className="text-sm text-cm-text-muted block mb-1">Reported by</label>
              <span className="text-sm text-cm-text-primary">{reportedByName}</span>
            </div>
          )}

          <div>
            <label className="text-sm text-cm-text-muted block mb-1">Status</label>
            <button
              onClick={toggleResolved}
              className={`px-4 py-2 rounded-lg font-mono text-sm font-medium transition-colors ${
                problem.resolved
                  ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
                  : 'bg-cm-accent-orange/20 text-cm-accent-orange hover:bg-cm-accent-orange/30'
              }`}
            >
              {problem.resolved ? 'Mark as Open' : 'Mark as Resolved'}
            </button>
          </div>

          <div className="flex gap-6 text-xs text-cm-text-muted">
            <span>Created: {new Date(problem.createdAt).toLocaleDateString()}</span>
            <span>Updated: {new Date(problem.updatedAt).toLocaleDateString()}</span>
            {problem.resolvedAt && (
              <span>Resolved: {new Date(problem.resolvedAt).toLocaleDateString()}</span>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProblemDetailPage;
